import { app } from '@/composition/app'
import { GlobalProfileState } from '@/composition/globalProfile'
import { GroupState } from '@/composition/groups'
import { PostState } from '@/composition/posts'
import { SearchState } from '@/composition/search'
import { UserState } from '@/composition/users'
import { IPosts } from '@/models/Posts'
import { MessageChatSquareIcon } from '@gohighlevel/ghl-icons/24/outline'
import { renderIcon } from '@gohighlevel/ghl-ui'
import { h } from 'vue'
import { checkChannelEditAccess } from './channel.helper'

export function updatePostDataInStates(
  newPostData: IPosts,
  groupSlug?: string,
  channelSlug?: string
): void {
  if (newPostData._id) {
    // updating in search state if the post is present in search results
    const postIndex = SearchState.state.searchDiscussionResults?.findIndex(
      post => post._id === newPostData._id
    )
    if (postIndex !== -1) {
      copyAttributes(
        newPostData,
        SearchState.state.searchDiscussionResults[postIndex]
      )
    }
    // find post in the post list and update the current_user_reactions and reactions
    const post = PostState.state.posts?.find(
      post => post._id == newPostData._id
    )

    if (post) {
      copyAttributes(newPostData, post)
    }

    const globalProfilePostIndex =
      GlobalProfileState.state.contributions?.contributions?.findIndex(
        post => post._id == newPostData._id
      )
    if (globalProfilePostIndex !== -1) {
      copyAttributes(
        newPostData,
        GlobalProfileState.state.contributions?.contributions[
          globalProfilePostIndex
        ]
      )
    }

    // check if the post is pinned in home timeline , if yes then update post data
    if (groupSlug) {
      const homeTimelinePinnedPost = PostState.state.pinnedPosts
        .get(groupSlug)
        ?.find(post => post._id == newPostData._id)

      if (homeTimelinePinnedPost) {
        copyAttributes(newPostData, homeTimelinePinnedPost)
      }
    }

    // check if the post is pinned in channel timeline , if yes then update the post data
    if (channelSlug) {
      const channelTimelinePinnedPost = PostState.state.pinnedPosts
        .get(channelSlug)
        ?.find(post => post._id == newPostData._id)
      if (channelTimelinePinnedPost) {
        copyAttributes(newPostData, channelTimelinePinnedPost)
      }
    }
  }
}

// need this function so that we only update the keys in the tagret object which are
// present in the source object, otherwise we could have used Object.assign() or spread operator
function copyAttributes(source: any, target: any) {
  for (const key in source) {
    target[key] = source[key]
      ? JSON.parse(JSON.stringify(source[key]))
      : undefined
  }
}

export function handlePostEdited(post: IPosts): void {
  const postRef = PostState.state.posts.find((p: IPosts) => p._id == post._id)

  // if post not found in post list then update the post data in states so
  // if may exists in pinned posts then it will be updated
  if (!postRef) {
    updatePostDataInStates(
      post,
      GroupState.state.groupData.slug,
      post.channelSlug
    )
    return
  }
  const outdatedPost = JSON.parse(JSON.stringify(postRef))
  if (outdatedPost) {
    outdatedPost.content = post.content
    outdatedPost.title = post.title
    outdatedPost.attachments = post.attachments
    outdatedPost.channelSlug = post.channelSlug
    outdatedPost.channelId = post.channelId
    outdatedPost.channelName = post.channelName
    outdatedPost.channelPinnedIndex = post.channelPinnedIndex
  }

  updatePostDataInStates(
    outdatedPost,
    GroupState.state.groupData.slug,
    outdatedPost.channelSlug
  )
}

export function shouldOpenPreview(file: any) {
  return (
    file?.status === 'completed' ||
    file?.status === 'uploaded' ||
    file?.provider_name
  )
}

export async function togglePostCommentsDisabled(
  post: IPosts,
  notification: any,
  t: (key: string) => string
) {
  // Handle undefined by explicitly setting to false
  const currentIsCommentDisabled = post.isCommentDisabled === true

  try {
    const updatedPost = await PostState.toggleCommentsDisabled(
      post._id as string,
      post.channelId,
      currentIsCommentDisabled
    )

    // Update the local post object reference
    post.isCommentDisabled = updatedPost.isCommentDisabled

    // Update post in search results and global profile if present
    updatePostDataInStates(
      updatedPost,
      GroupState.state.groupData?.slug,
      post.channelSlug
    )

    if (!app?.state?.isMobile) {
      notification.create({
        title: updatedPost.isCommentDisabled
          ? t('post.notifications.isCommentDisabled')
          : t('post.notifications.isCommentEnabled'),
        type: 'success',
        duration: 3000,
      })
    }

    return updatedPost
  } catch (error) {
    notification.create({
      title: t('post.notifications.error'),
      type: 'error',
      duration: 3000,
    })

    throw error
  }
}

/**
 * Adds the enable/disable comments menu option if the user is admin or post author
 * @param menuList - The menu array to modify (passed by reference)
 * @param postUserSlug - The post author's slug
 * @param isCommentDisabled - Whether comments are currently disabled
 * @param t - The i18n translation function
 */
export function addCommentsToggleMenuItem(
  menuList: any[],
  postUserSlug: string | undefined,
  isCommentDisabled: boolean | undefined,
  t: (key: string) => string
): void {
  const isPostAuthor = postUserSlug === UserState.state.currentUser?.slug

  if (checkChannelEditAccess() || isPostAuthor) {
    menuList.push({
      label: isCommentDisabled
        ? t('post.enableComments')
        : t('post.disableComments'),
      key: isCommentDisabled ? 'enableComments' : 'disableComments',
      class: 'rounded-2xl',
      props: {
        id: isCommentDisabled
          ? 'enable-comments-option'
          : 'disable-comments-option',
      },
      icon() {
        return h(renderIcon(MessageChatSquareIcon), null, {})
      },
    })
  }
}
